import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import { useSearchParams } from "react-router-dom";
import { adminApi } from "../../api/admin";

import EditorialIntro from "../../components/dashboard/EditorialIntro";
import ResponsiveListControls from "../../components/dashboard/ResponsiveListControls";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { useListScrollRestoration, usePersistentListValue } from "../../utils/listViewState";
import {
  activityActorLabel,
  activityDisplayDescription,
  activityDisplayTitle,
} from "../../utils/activityPresentation";
const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };
const PAGE_SIZE = 40;

const CATEGORY_LABELS = {
  navigation: "Navegación",
  commercial: "Comercial",
  auth: "Accesos",
  user: "Usuarios",
  project: "Proyectos",
  landing: "Página",
  product: "Productos / servicios",
  order: "Pedidos",
  payment: "Pagos",
  appointment: "Agenda",
  erp: "ERP",
  cash: "Caja",
  stock: "Stock",
  billing: "Billing",
  push: "Notificaciones",
  integration: "Integraciones",
  system: "Sistema",
};

const SEVERITY_COLOR = {
  info: "default",
  success: "success",
  warning: "warning",
  critical: "error",
};

function dateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(date);
}

function Metric({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, "&:last-child": { pb: { xs: 1.5, sm: 2.5 } } }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 900 }}>{title}</Typography>
            <Typography variant="h4" sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.55rem", sm: "2.125rem" }, lineHeight: 1.1 }}>{value}</Typography>
            <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{helper}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function SuperActivityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [journeys, setJourneys] = React.useState([]);
  const [pagination, setPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [push, setPush] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [filters, setFilters] = usePersistentListValue("super-activity:filters", {
    category: searchParams.get("category") || "",
    projectSlug: searchParams.get("project") || "",
    severity: searchParams.get("severity") || "",
    from: searchParams.get("from") || "",
    to: searchParams.get("to") || "",
    q: "",
  });
  const [page, setPage] = usePersistentListValue("super-activity:page", 1);
  const queryFilterKey = searchParams.toString();

  React.useEffect(() => {
    const incoming = {
      category: searchParams.get("category") || "",
      projectSlug: searchParams.get("project") || "",
      severity: searchParams.get("severity") || "",
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
    };
    if (!Object.values(incoming).some(Boolean)) return;
    setPage(1);
    setFilters((current) => {
      const next = { ...current, ...incoming };
      return Object.keys(incoming).every((key) => current?.[key] === next[key]) ? current : next;
    });
  // Los query params son el contrato de drill-down desde Super Panel.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilterKey]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [activityResponse, summaryResponse, journeysResponse, pushResponse] = await Promise.all([
        adminApi.activity({
          page,
          pageSize: PAGE_SIZE,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
        }),
        adminApi.activitySummary(24),
        adminApi.activityJourneys({ hours: 168, limit: 20 }),
        adminApi.pushStatus(),
      ]);
      setItems(activityResponse.items || []);
      setPagination(activityResponse.pagination || { page, pages: 1, total: 0 });
      setSummary(summaryResponse.summary || null);
      setJourneys(journeysResponse.items || []);
      setPush(pushResponse);

    } catch (err) {
      setError(err.message || "No se pudo cargar la actividad global");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  React.useEffect(() => {
    load();
  }, [load]);

  useListScrollRestoration("super-activity", !loading);

  if (loading && !summary && !items.length && !journeys.length) {
    return <DashboardLoadingState label="Cargando actividad global…" minHeight={420} />;
  }

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
    const next = new URLSearchParams(searchParams);
    if (["category", "projectSlug", "severity", "from", "to"].includes(key)) {
      const queryKey = key === "projectSlug" ? "project" : key;
      if (value) next.set(queryKey, value);
      else next.delete(queryKey);
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <Stack spacing={{ xs: 2, sm: 3 }} sx={{ minWidth: 0 }}>
      <EditorialIntro
        title="Actividad global"
        titleVariant="h3"
        context="Visitas y operaciones de toda la plataforma."
        helpTitle="Qué reúne la actividad global"
        helpText="Combina intención comercial y operaciones realizadas por clientes, equipos, automatizaciones y webhooks para investigar recorridos sin exponer detalle técnico de forma permanente."
        action={<Button onClick={load} disabled={loading} startIcon={<RefreshRoundedIcon />} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Actualizar</Button>}
      />

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Grid container spacing={2.5}>
        <Grid item xs={6} lg={3}>
          <Metric icon={<HistoryRoundedIcon />} title="Eventos en 24 h" value={summary?.total ?? "—"} helper="Operación y navegación" />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Metric icon={<PeopleAltRoundedIcon />} title="Visitantes únicos" value={summary?.uniqueVisitors ?? "—"} helper="Identidad anónima por navegador" />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Metric icon={<BoltRoundedIcon />} title="Alta intención" value={summary?.highIntent ?? "—"} helper="WhatsApp, carrito y checkout" />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Metric icon={<NotificationsActiveRoundedIcon />} title="Canal Owner" value={push?.subscribers ?? "—"} helper="Dispositivos del Platform Owner" />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Alertas globales</Typography>
              <Chip size="small" color="success" variant="outlined" label="Sólo Platform Owner" sx={{ fontWeight: 900 }} />
            </Stack>
            <Typography sx={{ mt: 0.4, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
              Las alertas que antes se administraban desde SuperAdmin ahora obedecen el mismo panel “Qué escuchar” del Owner. Ningún otro superadmin, usuario de proyecto o cliente puede recibir este canal global.
            </Typography>
          </Box>
          <Button
            href="/#/dashboard/owner/configuracion?seccion=notificaciones"
            startIcon={<NotificationsActiveRoundedIcon />}
            variant="contained"
            sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
          >
            Administrar en Owner
          </Button>
        </Stack>
        {!push?.configured && <Alert severity="warning" sx={{ mt: 2 }}>Faltan las variables VAPID del backend.</Alert>}
        <Typography sx={{ mt: 1.2, fontSize: ".72rem", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)" }}>
          Dispositivos Owner conectados: {push?.subscribers ?? 0}. Esta pantalla ya no crea suscripciones SuperAdmin independientes.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <RouteRoundedIcon sx={{ color: BRAND.orange }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Recorridos recientes</Typography>
            <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Sesiones de visitantes y usuarios del panel, agrupadas paso a paso.</Typography>
          </Box>
        </Stack>
        <Stack spacing={1.25}>
          {journeys.slice(0, 10).map((journey) => (
            <Box key={`${journey.projectId}-${journey.channel}-${journey.sessionId}`} sx={{ p: 2, borderRadius: 3, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)" }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={journey.channel === "storefront" ? "Web pública" : "Panel"} />
                    {journey.projectName ? <Chip size="small" variant="outlined" label={journey.projectName} /> : null}
                    <Chip size="small" variant="outlined" label={`${journey.stepsCount || 0} pasos`} />
                    {journey.highIntent ? <Chip size="small" color="success" label={`${journey.highIntent} acciones comerciales`} /> : null}
                  </Stack>
                  <Typography variant="body2" sx={{ display: { xs: "block", md: "none" }, mt: 1, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)", overflowWrap: "anywhere" }}>
                    {(journey.steps || []).slice(-3).map((step) => step.title || step.action).join(" → ") || "Sin pasos visibles"}
                  </Typography>
                  <Typography variant="body2" sx={{ display: { xs: "none", md: "block" }, mt: 1, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)" }}>
                    {(journey.steps || []).slice(-8).map((step) => step.title || step.action).join(" → ") || "Sin pasos visibles"}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", flexShrink: 0 }}>{dateTime(journey.lastAt)}</Typography>
              </Stack>
            </Box>
          ))}
          {!loading && !journeys.length && <Alert severity="info">Todavía no hay recorridos agrupados.</Alert>}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.1, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <ResponsiveListControls
          search={<TextField fullWidth size="small" placeholder="Buscar actividad" inputProps={{ "aria-label": "Buscar actividad, persona o entidad" }} value={filters.q} onChange={(event) => updateFilter("q", event.target.value)} />}
          activeFilterCount={[filters.category, filters.projectSlug, filters.severity, filters.from, filters.to].filter(Boolean).length}
          onClear={() => {
            setPage(1);
            setFilters((current) => ({ ...current, category: "", projectSlug: "", severity: "", from: "", to: "" }));
            const next = new URLSearchParams(searchParams);
            next.delete("category");
            next.delete("project");
            next.delete("severity");
            next.delete("from");
            next.delete("to");
            setSearchParams(next, { replace: true });
          }}
          drawerTitle="Filtrar actividad global"
        >
          <FormControl size="small" sx={{ minWidth: { sm: 180 } }}>
            <InputLabel>Categoría</InputLabel>
            <Select label="Categoría" value={filters.category} onChange={(event) => updateFilter("category", event.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Proyecto / slug" value={filters.projectSlug} onChange={(event) => updateFilter("projectSlug", event.target.value)} sx={{ minWidth: { sm: 180 } }} />
          <FormControl size="small" sx={{ minWidth: { sm: 160 } }}>
            <InputLabel>Severidad</InputLabel>
            <Select label="Severidad" value={filters.severity} onChange={(event) => updateFilter("severity", event.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="info">Información</MenuItem>
              <MenuItem value="success">Éxito</MenuItem>
              <MenuItem value="warning">Advertencia</MenuItem>
              <MenuItem value="critical">Crítica</MenuItem>
              <MenuItem value="warning,critical">Advertencia + crítica</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="Desde" value={filters.from || ""} onChange={(event) => updateFilter("from", event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: { sm: 165 } }} />
          <TextField size="small" type="date" label="Hasta" value={filters.to || ""} onChange={(event) => updateFilter("to", event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: { sm: 165 } }} />
        </ResponsiveListControls>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Historial</Typography>
            <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{pagination.total || 0} eventos encontrados</Typography>
          </Box>
          {loading && <WebProfitSpinner size={24} />}
        </Stack>

        <Stack spacing={1.25}>
          {items.map((event) => (
            <Box key={event.id} sx={{ p: 2, borderRadius: 3, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.05)" }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
                <Stack direction="row" spacing={1.4} sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>
                    {event.source === "storefront" ? <StorefrontRoundedIcon fontSize="small" /> : <AnalyticsRoundedIcon fontSize="small" />}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{activityDisplayTitle(event)}</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", mt: 0.25, display: { xs: "-webkit-box", md: "block" }, WebkitLineClamp: { xs: 2, md: "unset" }, WebkitBoxOrient: "vertical", overflow: { xs: "hidden", md: "visible" } }}>{activityDisplayDescription(event)}</Typography>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      <Chip size="small" label={CATEGORY_LABELS[event.category] || event.category} />
                      <Chip size="small" color={SEVERITY_COLOR[event.severity] || "default"} label={event.severity || "info"} />
                      {event.projectName ? <Chip size="small" variant="outlined" label={`${event.projectName}${event.projectSlug ? ` · ${event.projectSlug}` : ""}`} /> : null}
                      <Chip size="small" variant="outlined" label={activityActorLabel(event)} />
                    </Stack>
                  </Box>
                </Stack>
                <Box sx={{ flexShrink: 0, textAlign: { md: "right" } }}>
                  <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", fontWeight: 800 }}>{dateTime(event.occurredAt)}</Typography>
                  <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                    {event.source === "storefront" ? "Web pública" : event.source === "panel" ? "Panel" : event.source === "webhook" ? "Automatización" : "Sistema"}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
          {!loading && !items.length && <Alert severity="info">No hay actividad para estos filtros.</Alert>}
        </Stack>

        {pagination.pages > 1 && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Pagination page={pagination.page || page} count={pagination.pages} onChange={(_event, value) => setPage(value)} color="primary" />
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
